/**
 * O texto das mensagens do José, o Desk Tutor (docs/spec/desk-tutor.md): o Markdown leve que a IA
 * escreve (**negrito**, `comando`, [links](endereço), endereços soltos e listas com `-` ou `1.`) vira
 * uma árvore simples que o React desenha, sem HTML no texto. Os links só abrem endereços http(s).
 */

export type TutorInline =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; children: TutorInline[] }
  | { kind: 'code'; text: string }
  | { kind: 'link'; href: string; children: TutorInline[] };

export type TutorBlock = { kind: 'paragraph'; inlines: TutorInline[] } | { kind: 'list'; ordered: boolean; items: TutorInline[][] };

// `código`, **negrito**, [texto](http…) e o endereço solto, sem a pontuação da frase no fim
const INLINE = /`([^`\n]+)`|\*\*(.+?)\*\*|\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"*])/g;

export function parseTutorInline(text: string): TutorInline[] {
  const out: TutorInline[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    if (match.index > last) out.push({ kind: 'text', text: text.slice(last, match.index) });
    if (match[1] !== undefined) out.push({ kind: 'code', text: match[1] });
    else if (match[2] !== undefined) out.push({ kind: 'strong', children: parseTutorInline(match[2]) });
    else if (match[3] !== undefined) out.push({ kind: 'link', href: match[4]!, children: parseTutorInline(match[3]) });
    else out.push({ kind: 'link', href: match[5]!, children: [{ kind: 'text', text: match[5]! }] });
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
}

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBER = /^\s*\d+[.)]\s+(.*)$/;

/** Os parágrafos (as linhas seguidas ficam juntas, com a quebra) e as listas. */
export function parseTutorText(source: string): TutorBlock[] {
  const blocks: TutorBlock[] = [];
  let paragraph: string[] = [];
  const flush = () => {
    if (paragraph.length) blocks.push({ kind: 'paragraph', inlines: parseTutorInline(paragraph.join('\n')) });
    paragraph = [];
  };
  for (const line of source.replace(/\r\n?/g, '\n').split('\n')) {
    const bullet = BULLET.exec(line);
    const number = bullet ? null : NUMBER.exec(line);
    const item = bullet ?? number;
    if (item) {
      flush();
      const ordered = !!number;
      const previous = blocks.at(-1);
      const inlines = parseTutorInline(item[1]!);
      if (previous?.kind === 'list' && previous.ordered === ordered) previous.items.push(inlines);
      else blocks.push({ kind: 'list', ordered, items: [inlines] });
    } else if (line.trim() === '') {
      flush();
    } else {
      paragraph.push(line);
    }
  }
  flush();
  return blocks;
}
